/**
 * Shared date-range preset engine. Lifted out of `dashboard/utils/dateRange.ts`
 * so the Dashboard and Reports surfaces drive one identical control (presets +
 * dialog + trigger chip) rather than each re-implementing it.
 *
 * Persistence is parameterised by a `storageKeyPrefix` so each surface keeps its
 * own remembered range: the Dashboard uses `dashboard_*` (unchanged), Reports
 * uses `reports_*`.
 */

export type DateRangeFilterType =
  | 'two_months'
  | 'current_quarter'
  | 'current_year'
  | 'year_to_date'
  | 'last_3_months'
  | 'next_3_months'
  | 'last_3_next_2'
  | 'next_8_weeks'
  | 'custom';

export interface DateRangeFilter {
  type: DateRangeFilterType;
  startDate: Date;
  endDate: Date;
  label: string;
}

// Pure helpers, kept at module scope so `initializeDateRangeFilter` can recompute
// relative presets (e.g. "6 Months (±3)") against today's date on every load
// rather than serving the boundaries that were active when the user first saved
// the preset.
export function getDateRangeLabel(
  type: DateRangeFilterType,
  startDate: Date,
  endDate: Date,
): string {
  switch (type) {
    case 'two_months':
      return '2 Months (±1)';
    case 'current_quarter': {
      const quarter = Math.floor(startDate.getMonth() / 3) + 1;
      return `Q${quarter} ${startDate.getFullYear()}`;
    }
    case 'current_year':
      return `${startDate.getFullYear()}`;
    case 'year_to_date':
      return `YTD ${startDate.getFullYear()}`;
    case 'last_3_months':
      return '6 Months (±3)';
    case 'next_3_months':
      return '6 Months (±3)';
    case 'last_3_next_2':
      return 'Last 3 months + next 2';
    case 'next_8_weeks':
      return 'Next 8 weeks';
    case 'custom':
      return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
    default:
      return 'Custom Range';
  }
}

export function createDateRangeFilter(type: DateRangeFilterType): DateRangeFilter {
  const now = new Date();
  let startDate: Date;
  let endDate: Date;

  switch (type) {
    case 'two_months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setMonth(endDate.getMonth() + 1);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'current_quarter': {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), currentQuarter * 3, 1);
      const lastDayOfQuarter = new Date(now.getFullYear(), (currentQuarter + 1) * 3, 0);
      endDate = new Date(
        lastDayOfQuarter.getFullYear(),
        lastDayOfQuarter.getMonth(),
        lastDayOfQuarter.getDate(),
        23,
        59,
        59,
        999,
      );
      break;
    }
    case 'current_year':
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
      break;
    case 'year_to_date':
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      break;
    case 'last_3_months':
    case 'next_3_months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 3);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setMonth(endDate.getMonth() + 3);
      endDate.setHours(23, 59, 59, 999);
      break;
    // The People reports' presets (D8): whole months either side of this one,
    // and the eight weeks from today.
    case 'last_3_next_2':
      startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 3, 0, 23, 59, 59, 999);
      break;
    case 'next_8_weeks':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 8 * 7 - 1, 23, 59, 59, 999);
      break;
    default:
      startDate = new Date(now);
      endDate = new Date(now);
  }

  return {
    type,
    startDate,
    endDate,
    label: getDateRangeLabel(type, startDate, endDate),
  };
}

/**
 * Reads any persisted preference from localStorage under `${storageKeyPrefix}_*`.
 * For relative presets we always recompute the boundaries against the current
 * date, so a saved "6 Months (±3)" filter follows the user as time passes
 * instead of staying frozen at the date it was first saved. Only the explicit
 * `'custom'` type hydrates from stored ISO strings.
 *
 * `defaultType` is the preset used when nothing is persisted — the Dashboard
 * keeps `two_months`; Reports passes `year_to_date` (a to-date default whose
 * end never runs past today, AC-17).
 *
 * Storage can throw (blocked site data, some private windows), and this runs in
 * a `useState` initialiser on the Dashboard and Reports; a throw there would
 * blank the page, so an unreadable store reads as "nothing persisted".
 */
export function initializeDateRangeFilter(
  storageKeyPrefix = 'dashboard',
  defaultType: DateRangeFilterType = 'two_months',
): DateRangeFilter {
  let savedType: DateRangeFilterType | null = null;
  let savedStartDate: string | null = null;
  let savedEndDate: string | null = null;
  try {
    savedType = localStorage.getItem(
      `${storageKeyPrefix}_dateFilterType`,
    ) as DateRangeFilterType | null;
    savedStartDate = localStorage.getItem(`${storageKeyPrefix}_startDate`);
    savedEndDate = localStorage.getItem(`${storageKeyPrefix}_endDate`);
  } catch {
    return createDateRangeFilter(defaultType);
  }

  if (savedType === 'custom' && savedStartDate && savedEndDate) {
    const startDate = new Date(savedStartDate);
    const endDate = new Date(savedEndDate);
    return {
      type: 'custom',
      startDate,
      endDate,
      label: getDateRangeLabel('custom', startDate, endDate),
    };
  }

  if (savedType && savedType !== 'custom') {
    return createDateRangeFilter(savedType);
  }

  return createDateRangeFilter(defaultType);
}

/**
 * Persists a chosen range under `${storageKeyPrefix}_*`. Only the preset type is
 * kept for relative ranges so they recompute against today on next load; custom
 * ranges also store their explicit ISO boundaries. A store that refuses the
 * write only costs the next visit its remembered range, so the chosen range
 * still applies for this one.
 */
export function saveDateRangeFilter(
  filter: DateRangeFilter,
  storageKeyPrefix = 'dashboard',
): void {
  try {
    localStorage.setItem(`${storageKeyPrefix}_dateFilterType`, filter.type);
    if (filter.type === 'custom') {
      localStorage.setItem(`${storageKeyPrefix}_startDate`, filter.startDate.toISOString());
      localStorage.setItem(`${storageKeyPrefix}_endDate`, filter.endDate.toISOString());
    } else {
      localStorage.removeItem(`${storageKeyPrefix}_startDate`);
      localStorage.removeItem(`${storageKeyPrefix}_endDate`);
    }
  } catch {
    // Blocked or full storage: nothing to remember the range with.
  }
}
